import { useEffect, useState } from "react";
import { Engagement } from "@models/engagement.model.ts";
import Loader from "../../../../../../../components/common/loader/loader.tsx";
import { EngagementService } from "@services/engagements/engagementService.ts";
import { appStore } from '@store/appStore.ts'
import { usePaginator } from "@components/paginator/usePaginator.tsx";
import Paginator from "@components/paginator/Paginator.tsx";
import { engagementStore } from '../../../../../engagementStore.ts'
import { engagementEvents } from '../../../../../engagementEvents.ts'
import { useSnapshot } from "valtio/index";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Paths } from "src/paths";
import { DEFAULT_SORT, statusFilterCodes, type StatusFilter } from "./EngagementListHeader.tsx";

/*
    Columns the API can order by (CmdGetEngagementList.SORT_FIELDS). Sorting is
    a server concern: the list is paged, so re-ordering the rows on screen would
    only sort the current page while looking like it sorted everything.
*/
type SortKey = "client" | "name" | "type" | "created" | "visited" | "live" | "updated";

/*
    The header cells, in grid order. `sorted` is the sort key whose arrow lands here; a
    column with none is not sortable by clicking it, though Sort by may still order the list
    by it (Recently Updated has no column of its own).

    `title` carries the long form for the two heads abbreviated to fit. Both people columns
    are avatar-only, which is the only reason two of them fit without horizontal scroll at
    1280px -- an inline name on either brings it straight back.
*/
const COLUMNS: { key: SortKey | null; label: string; title?: string; cls?: string }[] = [
  { key: "client", label: "Customer" },
  { key: "name", label: "Engagement" },
  { key: "type", label: "Type", cls: "eng-col--type" },
  { key: "live", label: "Status" },
  { key: "created", label: "Created", cls: "eng-col--created" },
  { key: "visited", label: "Last Visited" },
  { key: null, label: "OPS", title: "Ops Lead" },
  { key: null, label: "CSM", title: "Client Success Manager" },
  { key: null, label: "" },
];

function personName(member: Engagement["engagement_manager"] | null): string | null {
  const person = member?.employee?.person;
  if (!person?.first_name) return null;
  return `${person.first_name} ${person.last_name ?? ""}`.trim();
}

function managerName(engagement: Engagement): string | null {
  return personName(engagement.engagement_manager);
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * The breakdown behind the Status tag.
 *
 * Spelled out rather than left to the count alone, because "2 Campaigns Live over 4
 * networks" invites the reader to multiply, and the whole point of the second line is that
 * it does not: it is the de-duplicated union across the engagement.
 */
function statusTitle(engagement: Engagement): string {
  const live = engagement.campaigns_live ?? 0;
  const networks = engagement.live_networks ?? [];
  const breakdown = (engagement.live_by_campaign ?? [])
    .map((row) => `${row.campaign}: ${row.networks.join(", ")}`)
    .join(" \u00b7 ");
  if (live === 0) return "No campaigns serving right now";
  return (
    `${plural(live, "campaign")} live \u00b7 ` +
    `${plural(networks.length, "unique network")} serving (${networks.join(", ")})` +
    (breakdown ? ` \u2014 ${breakdown}` : "")
  );
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

interface Props {
  managerIds?: number[];
  managerUnassigned?: boolean;
  csmIds?: number[];
  csmUnassigned?: boolean;
  mine?: boolean;
  /** Derived Live / Not Live. Undefined when both or neither is ticked. */
  live?: boolean;
  statusFilter?: StatusFilter;
  /** Show archived engagements instead of live ones (MPN-1533). */
  archived?: boolean;
  types?: string[];
  search?: string;
  onTotalChange?: (total: number) => void;
}

export const EngagementListTable = ({
  managerIds = [],
  managerUnassigned = false,
  csmIds = [],
  csmUnassigned = false,
  mine = false,
  live,
  statusFilter = new Set(["OPEN"]),
  archived = false,
  types = [],
  search = "",
  onTotalChange,
}: Props = {}) => {
  const [engagementList, setEngagementList] = useState<Engagement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rowMenu, setRowMenu] = useState<number | null>(null);
  const [confirmDeleteFor, setConfirmDeleteFor] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ text: string; undo?: () => void } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortParam = searchParams.get("sort") ?? DEFAULT_SORT;
  const sortKey = sortParam.replace(/^-/, "") as SortKey;
  const sortDescending = sortParam.startsWith("-");

  const initialLimit = Number(searchParams.get("limit")) || 50;
  const initialOffset = Number(searchParams.get("offset")) || 0;
  const paginator = usePaginator({ initialLimit, initialOffset });
  const engagementEventsSnap = useSnapshot(engagementEvents);

  // Cleared on any view change: a message about an engagement you archived in the live
  // list has no business still being on screen once you switch to the archived one.
  useEffect(() => setToast(null), [archived, statusFilter, search, types.join(",")])

  const canArchive =
    appStore.loggedInUser?.permissions?.hasSomeAccess?.("Platform Admin") ?? false

  /**
   * Flip an engagement's client visibility, then drop it from the current view.
   *
   * Removed locally rather than refetched: the row no longer belongs in the list you are
   * looking at, and leaving it until a refetch would let someone archive the same thing
   * twice. The list is a switch between two disjoint sets, so anything toggled has by
   * definition moved to the other one.
   */
  /**
   * Flip an engagement's client visibility, then drop it from the current view.
   *
   * Removed locally rather than refetched: the row no longer belongs in the list you are
   * looking at, and leaving it until a refetch would let someone archive the same thing
   * twice. The list is a switch between two disjoint sets, so anything toggled has by
   * definition moved to the other one.
   *
   * Archiving still asks nothing before acting, and now offers Undo instead.
   *
   * Rachel's first question on this feature was "where should I expect to find the
   * engagement once I archive it" -- she archived one, the row vanished, and nothing said it
   * was recoverable. A confirmation dialog was the obvious answer and the wrong one:
   * archiving is reversible, and confirming a safe action trains people to click through the
   * delete confirmation on this very list, which is not. Undo answers the same question
   * without spending that.
   */
  const handleArchiveToggle = async (item: Engagement) => {
    const next = !item.is_archived
    setRowMenu(null)
    try {
      await new EngagementService().setEngagementArchived(item.id, next)
      setEngagementList((rows) => rows.filter((r) => r.id !== item.id))
      // Undo AND where it went. They answer different questions: Undo is for the archive
      // you did not mean, and naming the filter is for the one you did and want back next
      // week, which was Rachel's actual question. Dropping the second when Undo arrived
      // would have quietly un-fixed MPN-1533.
      setToast({
        text: next
          ? `${item.name || "Engagement"} archived. Find it under the Archived filter.`
          : `${item.name || "Engagement"} restored, and clients can see it again.`,
        undo: async () => {
          try {
            await new EngagementService().setEngagementArchived(item.id, !next)
            setToast(null)
            // Refetched rather than spliced back, because the row has to return to its
            // right place in the current sort, and putting it back on the end would be a
            // lie about where it belongs.
            setReloadKey((key) => key + 1)
          } catch (error) {
            console.error(error)
            setToast({ text: "That did not undo. Nothing has changed." })
          }
        },
      })
    } catch (error: any) {
      console.log("ERROR ", error)
      setToast({ text: "That did not save. Nothing has changed." })
    }
  }

  useEffect(() => {
    const getEngagementList = async () => {
      setIsLoading(true)
      try {
        const service = new EngagementService()
        const response = await service.getEngagementsList(
          appStore.loggedInUser.organization.id,
          paginator.state.limit,
          paginator.state.offset,
          {
            managerIds,
            managerUnassigned,
            csmIds,
            csmUnassigned,
            mine,
            live,
            status: statusFilterCodes(statusFilter),
            type: types,
            search,
            sort: sortParam,
            archived,
          },
        )
        paginator.events.setSizeOfCurrentPage(response.length)
        setEngagementList(response)
        onTotalChange?.(response.length)
      } catch (error: any) {
        console.log("ERROR ", error)
      } finally {
        setIsLoading(false)
      }
    }

    getEngagementList()
     
  }, [
    appStore.loggedInUser.organization.id,
    engagementEventsSnap.ENGAGEMENT_CREATED,
    engagementEventsSnap.ENGAGEMENT_DELETED,
    engagementEventsSnap.ENGAGEMENT_NAME_UPDATED,
    reloadKey,
    paginator.state.limit,
    paginator.state.offset,
    managerIds.join(","),
    managerUnassigned,
    csmIds.join(","),
    csmUnassigned,
    mine,
    live,
    ...statusFilter,
    types.join(","),
    search,
    sortParam,
    archived,
  ])

  // Sync paginator state to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("limit", String(paginator.state.limit));
    params.set("offset", String(paginator.state.offset));
    setSearchParams(params, { replace: true });
     
  }, [paginator.state.limit, paginator.state.offset]);

  const handleSort = (key: SortKey) => {
    // Same column toggles direction; a new column starts ascending, except
    // Created, where "newest first" is what anyone means by sorting by date.
    const next =
      sortKey === key ? (sortDescending ? key : `-${key}`) : key === "created" ? "-created" : key;
    const params = new URLSearchParams(searchParams);
    params.set("sort", next);
    params.set("offset", "0");
    setSearchParams(params, { replace: true });
    paginator.events.setOffset(0);
  };

  const openEngagement = (engagement: Engagement) => {
    engagementStore.selectedEngagement = engagement;
    engagementStore.listSearchParams = searchParams.toString();
    navigate(`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagement.id}/todos`);
  };

  /*
      Delete confirms in a popover on the row rather than in a type-to-confirm modal.

      That is a lighter guard than what was here, and it is the design's call rather than an
      oversight, so it is worth saying why it holds. The typed confirmation was protecting a
      bare trash icon sitting next to the chevron -- one mis-click away from opening the
      engagement. Delete now lives inside a kebab that is invisible until the row is hovered,
      so reaching it is already deliberate, and the popover makes it two deliberate clicks.
      The protection moved rather than went away.

      If this ever goes back to a one-click control, the typed confirmation should come back
      with it: deleting takes the engagement's campaigns, ad groups and schedule with it.
  */
  const confirmDelete = async (item: Engagement) => {
    setIsDeleting(true);
    try {
      await new EngagementService().deleteEngagement(item.id);
      setEngagementList((prev) => {
        const updated = prev.filter((e) => e.id !== item.id);
        paginator.events.setSizeOfCurrentPage(updated.length);
        return updated;
      });
      engagementEvents.send_event("ENGAGEMENT_DELETED")
      // No Undo: the row is gone from the database, and offering one would be a button that
      // cannot do what it says.
      setToast({ text: `${item.name || "Engagement"} deleted.` });
    } catch (e) {
      console.error(e);
      setToast({ text: "That did not delete. Nothing has changed." });
    } finally {
      setIsDeleting(false);
      setConfirmDeleteFor(null);
      setRowMenu(null);
    }
  };

  const from = paginator.state.offset + 1;
  const to = paginator.state.offset + engagementList.length;

  return (
    <>
      <div className="eng-table-wrap">
        {isLoading && <Loader />}

        {!isLoading && engagementList.length === 0 ? (
          <div className="eng-empty">
            <div className="eng-empty__title">No engagements match</div>
            <div className="eng-empty__body">
              {search || types.length || mine || live !== undefined
                || managerIds.length || csmIds.length || managerUnassigned || csmUnassigned
                ? "Nothing here with those filters. Clear one and try again."
                : "Create an engagement to get started."}
            </div>
          </div>
        ) : (
          <table className="eng-table">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column.label || "actions"}
                    title={column.title}
                    className={[
                      column.cls,
                      column.key
                        ? `eng-table__sortable${
                            sortKey === column.key
                              ? sortDescending
                                ? " eng-table__sortable--desc"
                                : " eng-table__sortable--asc"
                              : ""
                          }`
                        : "",
                    ].filter(Boolean).join(" ") || undefined}
                    onClick={column.key ? () => handleSort(column.key as SortKey) : undefined}
                    aria-sort={
                      column.key && sortKey === column.key
                        ? sortDescending
                          ? "descending"
                          : "ascending"
                        : undefined
                    }
                  >
                    {column.label}
                    {/* Only on the active column. An arrow on every sortable head says
                        nothing about which one is in force. */}
                    {column.key === sortKey && (
                      <i
                        className={`bi ${sortDescending ? "bi-arrow-down" : "bi-arrow-up"} eng-table__arrow`}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {engagementList.map((item: Engagement) => {
                const manager = managerName(item);
                const csm = personName(item.client_success_manager);
                const liveCount = item.campaigns_live ?? 0;
                const networks = item.live_networks ?? [];
                return (
                  <tr
                    key={item.id}
                    className="eng-row"
                    tabIndex={0}
                    role="button"
                    onClick={() => openEngagement(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openEngagement(item);
                      }
                    }}
                  >
                    <td className="eng-row__client" title={item.client?.name}>{item.client?.name}</td>
                    <td className="eng-row__name" title={item.name || "Untitled"}>
                      {item.name ? item.name : <span className="eng-row__untitled">Untitled</span>}
                    </td>
                    <td className="eng-col--type">
                      <span className={`eng-type eng-type--${item.type?.toLowerCase()}`}>
                        {item.type}
                      </span>
                    </td>
                    {/*
                        Two stacked lines, and the second is always rendered. Hidden rather
                        than omitted when nothing is serving, so every row is the same height
                        and the tags line up down the column instead of jittering.
                    */}
                    <td className="eng-status" title={statusTitle(item)}>
                      <span className={`eng-status__tag${liveCount ? " eng-status__tag--live" : ""}`}>
                        {liveCount ? `${plural(liveCount, "Campaign")} Live` : "Not Live"}
                      </span>
                      <span
                        className="eng-status__nets"
                        style={liveCount ? undefined : { visibility: "hidden" }}
                      >
                        {plural(networks.length, "network")}
                      </span>
                    </td>
                    <td className="eng-row__ago eng-col--created">{item.create_date.timeAgo()}</td>
                    <td className="eng-row__ago">
                      {item.last_viewed
                        ? item.last_viewed.timeAgo()
                        : <span className="eng-row__untitled">Never</span>}
                    </td>
                    <td className="eng-people" title={manager ?? "Unassigned"}>
                      {manager
                        ? <span className="eng-avatar">{initials(manager)}</span>
                        : <span className="eng-avatar eng-avatar--none">—</span>}
                    </td>
                    <td className="eng-people" title={csm ?? "Unassigned"}>
                      {csm
                        ? <span className="eng-avatar eng-avatar--csm">{initials(csm)}</span>
                        : <span className="eng-avatar eng-avatar--none">—</span>}
                    </td>
                    <td className="eng-row__end">
                      {/*
                          One kebab instead of two bare icons. The archive and trash icons
                          sat a few pixels from the chevron and only said what they did on
                          hover, so the mis-click they invited was "open" versus "delete".
                      */}
                      <div className="eng-kebab" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="eng-kebab__btn"
                          aria-label="Row actions"
                          aria-expanded={rowMenu === item.id}
                          onClick={() => {
                            setConfirmDeleteFor(null);
                            setRowMenu(rowMenu === item.id ? null : item.id);
                          }}
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </button>

                        {rowMenu === item.id && confirmDeleteFor !== item.id && (
                          <div className="eng-kebab__pop">
                            {canArchive && (
                              <button
                                type="button"
                                className="eng-kebab__item"
                                onClick={() => void handleArchiveToggle(item)}
                              >
                                <i className="bi bi-archive" />
                                {item.is_archived ? "Restore" : "Archive"}
                              </button>
                            )}
                            <button
                              type="button"
                              className="eng-kebab__item eng-kebab__item--danger"
                              onClick={() => setConfirmDeleteFor(item.id)}
                            >
                              <i className="bi bi-trash" />
                              Delete
                            </button>
                          </div>
                        )}

                        {confirmDeleteFor === item.id && (
                          <div className="eng-kebab__pop eng-confirm">
                            <p className="eng-confirm__text">
                              Delete this engagement? This can’t be undone.
                            </p>
                            <div className="eng-confirm__actions">
                              <button
                                type="button"
                                className="eng-confirm__cancel"
                                disabled={isDeleting}
                                onClick={() => { setConfirmDeleteFor(null); setRowMenu(null); }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="eng-confirm__go"
                                disabled={isDeleting}
                                onClick={() => void confirmDelete(item)}
                              >
                                {isDeleting ? "Deleting…" : "Delete"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Weighted on hover and set well apart from the kebab, so the two do
                          not read as one cluster of controls. */}
                      <i className="bi bi-chevron-right eng-row__chev" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="eng-foot">
        <span className="eng-foot__range">
          {engagementList.length > 0 ? `${from}–${to}` : "0"} shown
        </span>
        <Paginator paginator={paginator} />
      </div>

      {toast && (
        <div className="eng-toast" role="status">
          <span>{toast.text}</span>
          {toast.undo && (
            <button type="button" className="eng-toast__undo" onClick={() => void toast.undo?.()}>
              Undo
            </button>
          )}
          <button
            type="button"
            className="eng-toast__close"
            aria-label="Dismiss"
            onClick={() => setToast(null)}
          >
            <i className="bi bi-x" />
          </button>
        </div>
      )}
    </>
  )
};
